import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { GoldenCase } from "@/services/adminModelCurrency";
import ScoreControl from "./ScoreControl";
import TranscriptView from "./TranscriptView";
import { READ_ONLY_NOTE, missingDimensions } from "./helpers";

interface LabelingFormProps {
  goldenCase: GoldenCase;
  writable: boolean;
  saving: boolean;
  onSave: (scores: Record<string, number>, notes: string) => Promise<unknown>;
  onSkip: () => void;
}

/** One transcript plus a score for each dimension. Remount (key by case id) to reset between cases. */
const LabelingForm = ({ goldenCase, writable, saving, onSave, onSkip }: LabelingFormProps) => {
  const [scores, setScores] = useState<Record<string, number>>({});
  const [notes, setNotes] = useState("");
  const [validation, setValidation] = useState<string | null>(null);

  async function handleSave() {
    const missing = missingDimensions(goldenCase.dimensions, scores);
    if (missing.length > 0) {
      setValidation(`Please score every item before saving. Still needed: ${missing.join(", ")}.`);
      return;
    }
    setValidation(null);
    await onSave(scores, notes);
  }

  return (
    <div className="space-y-4">
      <TranscriptView goldenCase={goldenCase} />
      <div className="space-y-3">
        {goldenCase.dimensions.map(dimension => (
          <ScoreControl
            key={dimension.key}
            dimension={dimension}
            value={scores[dimension.key]}
            onChange={score => setScores(prev => ({ ...prev, [dimension.key]: score }))}
          />
        ))}
      </div>
      <div className="space-y-1">
        <Label htmlFor="golden-notes">Notes (optional)</Label>
        <Textarea id="golden-notes" value={notes} onChange={e => setNotes(e.target.value)} />
      </div>
      {validation ? (
        <p role="alert" className="text-sm text-destructive">
          {validation}
        </p>
      ) : null}
      {!writable ? <p className="text-sm text-muted-foreground">{READ_ONLY_NOTE}</p> : null}
      <div className="flex gap-2">
        <Button onClick={handleSave} disabled={!writable || saving}>
          {saving ? "Saving…" : "Save and next"}
        </Button>
        <Button variant="outline" onClick={onSkip} disabled={saving}>
          Skip
        </Button>
      </div>
    </div>
  );
};

export default LabelingForm;
